import { Suspense, forwardRef } from "react";
import { styled } from "@mui/material";
import { MapContainer, MapContainerProps } from "react-leaflet";

import { useMapPosition } from "hooks/mapHooks";

import { MapSizeObserver } from "./utils/MapSizeObserver";
import { PersistMapPosition } from "./utils/PersistMapPosition";
import { SelectBaseLayersControl } from "./layers/select/SelectBaseLayersControl";
import { RenderOverlayers } from "./layers/RenderOverlayers";
import { RenderBackgroundLayer } from "./layers/RenderBackgroundLayer";
import { FullscreenControl } from "./FullScreenControl";
import { MapZoomControls } from "./MapZoomControls";
import { AttributionAndScale } from "./attribution/AttributionAndScale";
import { GeomanLanguage } from "./utils/GeomanLanguage";
import { RulerControl } from "./utils/RulerControl";
import { RenderPersonalLayers } from "./layers/RenderPersonalLayers";
import { LegendControl } from "./legend/legendControl/LegendControl";

interface Props extends MapContainerProps {
  setMapRef: (mapRef: L.Map | null) => void;
  uuid?: string;
}

export const MapContent = forwardRef((props: Props, ref) => {
  const { setMapRef, children, uuid } = props;
  const { zoom, lat, lng } = useMapPosition();

  return (
    <StyledMapContainer
      center={[lat, lng]}
      zoom={zoom}
      zoomControl={false}
      attributionControl={false}
      ref={(ref) => setMapRef(ref)}
      data-tour="step-1"
    >
      <PersistMapPosition />
      <MapSizeObserver />
      <RenderBackgroundLayer />
      <Suspense fallback={<></>}>
        <RenderOverlayers />
      </Suspense>
      <Suspense fallback={<></>}>
        <RenderPersonalLayers />
      </Suspense>

      <AttributionAndScale position="bottomright" uuid={uuid} />
      <MapZoomControls position="bottomright" />
      <FullscreenControl position="bottomright" />
      <Suspense fallback={<></>}>
        <LegendControl position="bottomright" />
      </Suspense>
      <GeomanLanguage />
      {children}
      <SelectBaseLayersControl position="topright" />
      <RulerControl position="topright" />
    </StyledMapContainer>
  );
});

const StyledMapContainer = styled(MapContainer)`
  .leaflet-right .leaflet-control.flush-to-edge {
    margin-right: 0px;
  }
  .leaflet-bottom .leaflet-control.flush-to-edge {
    margin-bottom: 0px;
  }
  .leaflet-left .leaflet-control.flush-to-edge {
    margin-left: 0px;
  }
  .leaflet-top .leaflet-control.flush-to-edge {
    margin-top: 0px;
  }
`;
